import { Link } from 'react-router-dom'

import { QRGenerator } from '../components/QRGenerator'
import { Badge, Card, CodeBlock, cx } from '../components/ui'

const CURL_EXAMPLE = `curl -X POST https://qr24.app/api/v1/qr/generate \\
  -H "Authorization: Bearer qr24_live_..." \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -H "Content-Type: application/json" \\
  -d '{
    "version": "v2",
    "payment": {
      "beneficiaryName": "ТОВ \\"Ромашка\\"",
      "iban": "UA213223130000026007233566001",
      "beneficiaryCode": "32129530",
      "amount": "1250.00",
      "purpose": "Оплата за рахунком №12/8"
    },
    "render": { "format": "png", "scale": 10 }
  }' --output qr.png`

const FEATURES = [
  {
    title: 'Три версії стандарту',
    body: 'Формати 001, 002 і 003 — з перевіркою IBAN за ISO 13616, коду ЄДРПОУ/РНОКПП та бюджету payload у байтах.',
    icon: 'layers',
  },
  {
    title: 'Миттєві перекази та блокування полів',
    body: 'Формат 003: ICT/XCT для миттєвих переказів, код заборони зміни полів і строк дії рахунку.',
    icon: 'shield',
  },
  {
    title: 'Будь-який формат',
    body: 'PNG, SVG, PDF, EPS або data-URL. Вектор — для поліграфії, растр — для екрана, payload — для власного рендера.',
    icon: 'file',
  },
  {
    title: 'Ідемпотентність із коробки',
    body: 'Заголовок Idempotency-Key: повтор запиту після таймауту поверне ту саму відповідь і не спише квоту.',
    icon: 'repeat',
  },
  {
    title: 'Пакетна генерація',
    body: 'До 500 QR за один запит. Помилковий рядок не зупиняє решту — у відповіді видно, який саме і чому.',
    icon: 'grid',
  },
  {
    title: 'Прозорі квоти',
    body: 'Кожна відповідь несе X-Quota-Remaining і X-Quota-Reset — ваш код бачить залишок без окремого запиту.',
    icon: 'gauge',
  },
  {
    title: 'Тестовий ключ одразу',
    body: 'Ключ qr24_test_… працює повністю, але нічого не тарифікує. Можна ганяти CI скільки завгодно.',
    icon: 'file',
  },
]

const STEPS = [
  { n: 1, title: 'Заповніть реквізити', body: 'Отримувач, IBAN, сума та призначення платежу.' },
  { n: 2, title: 'Перевірте попередній перегляд', body: 'QR оновлюється миттєво, помилки підсвічуються у полі.' },
  { n: 3, title: 'Завантажте або підключіть API', body: 'PNG/SVG в один клік — або той самий результат через HTTP.' },
]

export function Landing() {
  return (
    <>
      {/* ------------------------------------------------------------ hero */}
      <section className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,theme(colors.brand.100),transparent)] dark:bg-[radial-gradient(60%_50%_at_50%_0%,theme(colors.brand.950),transparent)]"
        />
        <div className="container-page relative py-16 sm:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <Badge tone="brand" className="mb-5">
              Стандарт НБУ · v1 · v2 · v3
            </Badge>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Платіжні QR-коди
              <span className="block text-brand-600 dark:text-brand-400">за стандартом НБУ</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-600 dark:text-slate-300">
              Створіть QR для кредитового переказу просто у браузері — або підключіть API
              і генеруйте тисячі кодів із рахунків, накладних та каси.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a href="#generator" className="btn-primary px-6 py-3 text-base">
                Спробувати генератор
              </a>
              <Link to="/docs" className="btn-secondary px-6 py-3 text-base">
                Документація API
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              Без реєстрації · 200 безкоштовних запитів API на місяць · картка не потрібна
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------- generator */}
      <section id="generator" className="container-page scroll-mt-20 py-16">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Спробуйте просто зараз
            </h2>
            <p className="mt-2 text-slate-600 dark:text-slate-400">
              Форма побудована з того самого опису стандарту, який валідує API.
            </p>
          </div>
          <Link to="/generator" className="btn-secondary">
            Відкрити повний генератор →
          </Link>
        </div>
        <QRGenerator compact />
      </section>

      {/* -------------------------------------------------------- features */}
      <section className="border-y border-slate-200 bg-white py-16 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Все, чого бракує саморобній інтеграції
            </h2>
            <p className="mt-3 text-slate-600 dark:text-slate-400">
              Формат payload — це десять рядків тексту. Решта складності починається там,
              де зʼявляються ретраї, ліміти й кирилиця в кодуванні.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature) => (
              <Card key={feature.title} className="h-full">
                <FeatureIcon name={feature.icon} />
                <h3 className="mt-4 font-semibold">{feature.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{feature.body}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ code */}
      <section className="container-page py-16">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Один HTTP-запит — готовий PNG
            </h2>
            <p className="mt-3 text-slate-600 dark:text-slate-400">
              Ніяких SDK і залежностей: звичайний JSON, зрозумілі помилки з назвою поля,
              бінарна відповідь без base64-обгортки.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                'Помилки завжди мають однакову структуру та стабільний код',
                'Заголовки X-Quota-* показують залишок після кожного виклику',
                '/qr/validate та /qr/decode безкоштовні й не витрачають квоту',
                'Тестовий ключ не тарифікується — тримайте його в CI',
              ].map((item) => (
                <li key={item} className="flex gap-2.5">
                  <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="m5 12 5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="text-slate-600 dark:text-slate-300">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/docs" className="btn-primary mt-8">
              Читати документацію
            </Link>
          </div>
          <CodeBlock code={CURL_EXAMPLE} title="curl" />
        </div>
      </section>

      {/* ----------------------------------------------------------- steps */}
      <section className="border-t border-slate-200 bg-white py-16 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="container-page">
          <h2 className="text-center text-2xl font-bold tracking-tight sm:text-3xl">
            Три кроки до першого QR
          </h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            {STEPS.map((step) => (
              <div key={step.n} className="relative text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-brand-600 text-lg font-bold text-white">
                  {step.n}
                </div>
                <h3 className="mt-4 font-semibold">{step.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="container-page py-16">
        <div className="overflow-hidden rounded-3xl bg-brand-700 px-6 py-14 text-center text-white sm:px-14">
          <h2 className="text-3xl font-bold tracking-tight">Готові інтегрувати?</h2>
          <p className="mx-auto mt-3 max-w-xl text-brand-50">
            Реєстрація займає хвилину, тестовий ключ видається одразу, а безкоштовний
            тариф не має терміну дії.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register" className="btn bg-white px-6 py-3 text-base text-brand-700 hover:bg-brand-50">
              Створити акаунт
            </Link>
            <Link to="/pricing" className="btn border border-brand-400 px-6 py-3 text-base text-white hover:bg-brand-600">
              Подивитися тарифи
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}

function FeatureIcon({ name }: { name: string }) {
  const paths: Record<string, string> = {
    layers: 'M12 3 3 8l9 5 9-5-9-5ZM3 13l9 5 9-5M3 18l9 5 9-5',
    file: 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z',
    repeat: 'M17 2l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3',
    grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
    gauge: 'M12 21a9 9 0 1 1 9-9M12 12l5-3',
    shield: 'M12 3l8 3v6c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V6l8-3Z',
  }
  return (
    <div className={cx('grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400')}>
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d={paths[name] ?? paths.layers} />
      </svg>
    </div>
  )
}
